import {fire, on} from 'delegated-events'
import {onInput} from '@github-ui/onfocus'

let changedPrivacyManually = false
const urlSearchParams = new URLSearchParams(window.location.search)
const profileReadmeParam = urlSearchParams.get('profile_readme')

function handleOwnerChange() {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const name = document.querySelector<HTMLElement>('.js-repo-name')!
  // Trigger auto-check to ensure repo name is available under the new org selected
  fire(name, 'input')

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const selectedOwner = document.querySelector<HTMLElement>('.js-owner-container [aria-checked="true"]')!

  const allowPublicRepos = selectedOwner.getAttribute('data-org-allow-public-repos') !== 'false'
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const publicRadio = document.querySelector<HTMLInputElement>('.js-privacy-toggle[value=public]')!
  const publicLabel = document.querySelector<HTMLElement>('.js-privacy-toggle-label-public')
  const publicDescription = document.querySelector<HTMLElement>('.js-public-description')

  enableDisableRepoType(allowPublicRepos, publicRadio, publicLabel, publicDescription)

  const businessId = selectedOwner.getAttribute('data-business-id')
  const internalRadio = updateInternalDiv(businessId, selectedOwner)

  const allowPrivateRepos = selectedOwner.getAttribute('data-org-allow-private-repos') !== 'false'
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const privateRadio = document.querySelector<HTMLInputElement>('.js-privacy-toggle[value=private]')!
  const privateLabel = document.querySelector<HTMLElement>('.js-privacy-toggle-label-private')
  const privateDescription = document.querySelector<HTMLElement>('.js-private-description')

  enableDisableRepoType(allowPrivateRepos, privateRadio, privateLabel, privateDescription)

  checkOwnerSelected()

  const privateRestrictedByPlan = selectedOwner.getAttribute('data-org-private-restricted-by-plan') !== 'false'
  const displayUpsell = selectedOwner.getAttribute('data-org-show-upgrade') !== 'false'
  const orgName = selectedOwner.getAttribute('data-org-name')
  const orgUpgradeLink = orgName ? document.querySelector<HTMLElement>(`a[data-upgrade-link="${orgName}"]`) : null
  if (allowPrivateRepos || !privateRestrictedByPlan) {
    if (orgUpgradeLink) orgUpgradeLink.hidden = true
  } else {
    if (orgUpgradeLink) orgUpgradeLink.hidden = !displayUpsell
  }

  // Handle trade restrictions on private repos
  const tradeRestricted = selectedOwner.getAttribute('data-org-show-trade-controls') === 'true'
  const individualTradeRestricted = selectedOwner.getAttribute('data-user-show-trade-controls') === 'true'
  const orgRestrictedWithoutPrivateRepos = tradeRestricted && !allowPrivateRepos

  // Hide all the plan/org policy stuff if there is a trade restriction in place.
  if (individualTradeRestricted || orgRestrictedWithoutPrivateRepos) {
    // Hide/Disable the rest
    privateRadio.disabled = true
    if (privateDescription) privateDescription.hidden = true
    if (orgUpgradeLink) orgUpgradeLink.hidden = true
  }

  ensureOneRadioIsSelected(selectedOwner, publicRadio, internalRadio, privateRadio)
  togglePermissionFields(selectedOwner.getAttribute('data-permission') === 'yes')
  handlePrivacyChange()

  validate()
}

function enableDisableRepoType(
  enabled: boolean,
  radio: HTMLInputElement | null,
  label: HTMLElement | null,
  description: HTMLElement | null,
) {
  if (enabled) {
    if (radio) radio.disabled = false
    if (label) label.classList.remove('color-fg-muted')
    if (description) description.hidden = false
  } else {
    if (radio) radio.disabled = true
    if (label) label.classList.add('color-fg-muted')
    if (description) description.hidden = true
  }
}

function checkOwnerSelected() {
  const selectedOwnerElement = getSelectedOwnerElement()
  if (!selectedOwnerElement) {
    return
  }

  const ownerFormGroup = selectedOwnerElement.closest('.form-group')
  if (!ownerFormGroup) {
    return
  }
}

function getSelectedOwnerElement() {
  return document.querySelector<HTMLElement>('.js-owner-container [aria-checked="true"]')
}

function ensureOneRadioIsSelected(
  selectedOwner: HTMLElement,
  publicRadio: HTMLInputElement | null,
  internalRadio: HTMLInputElement | null,
  privateRadio: HTMLInputElement,
) {
  // Determine the best default visibility to select based on the org's default and what's enabled.
  let selectRadio = null
  if (selectedOwner.getAttribute('data-default') === 'private' && privateRadio && !privateRadio.disabled)
    selectRadio = privateRadio
  else if (selectedOwner.getAttribute('data-default') === 'internal' && internalRadio && !internalRadio.disabled)
    selectRadio = internalRadio
  else if (publicRadio && !publicRadio.disabled) selectRadio = publicRadio
  else if (internalRadio && !internalRadio.disabled) selectRadio = internalRadio

  if (privateRadio && !privateRadio.disabled && profileReadmeParam === 'member') selectRadio = privateRadio
  else if (publicRadio && !publicRadio.disabled && profileReadmeParam) selectRadio = publicRadio

  // Not possible because orgs with no enabled visibilities can't be selected, but flow doesn't know that so here we are.
  if (!selectRadio) return

  // Is a disabled option currently selected?
  const disabledSelected =
    (publicRadio && publicRadio.disabled && publicRadio.checked) ||
    (privateRadio.disabled && privateRadio.checked) ||
    (internalRadio && internalRadio.disabled && internalRadio.checked)

  // Are none of the visible options currently selected?
  const noneSelected =
    (!publicRadio || !publicRadio.checked) && (!internalRadio || !internalRadio.checked) && !privateRadio.checked

  // If the user hasn't manually changed visibility or we're in an invalid state, either because
  //  nothing is selected or a disabled option is selected, select the best available default.
  if (changedPrivacyManually === false || disabledSelected === true || noneSelected === true) {
    selectRadio.checked = true
    fire(selectRadio, 'change')
  }
}

// Display only the currently selected org's internal repos div, if any, and
// update its enabled/disabled state and labels.
function updateInternalDiv(businessId: string | null, selectedOwner: HTMLElement): HTMLInputElement | null {
  if (businessId) {
    const selectedDiv = document.querySelector<HTMLElement>(`#new-repo-internal-visibility-${businessId}`)
    if (selectedDiv) {
      selectedDiv.hidden = false

      const internalRadio = selectedDiv.querySelector('.js-privacy-toggle[value=internal]')
      if (internalRadio instanceof HTMLInputElement) {
        if (selectedOwner.getAttribute('data-org-allow-internal-repos') === 'false') {
          internalRadio.disabled = true
        } else {
          internalRadio.disabled = false
        }
        return internalRadio
      }
    }
  }
  return null
}

function togglePermissionFields(hasPermission: boolean) {
  for (const field of document.querySelectorAll<HTMLElement>('.js-with-permission-fields')) {
    field.hidden = !hasPermission
  }

  const erroredEl = document.querySelector<HTMLElement>('.errored')
  const warnEl = document.querySelector<HTMLElement>('dl.warn')
  if (erroredEl) erroredEl.hidden = !hasPermission
  if (warnEl) warnEl.hidden = !hasPermission
}

function handlePrivacyChange(event?: Event) {
  const radio =
    (event?.target as HTMLInputElement | null) || document.querySelector<HTMLInputElement>('.js-privacy-toggle:checked')

  if (!radio) {
    return
  }

  validate()
}

function validate() {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const form = document.querySelector<HTMLElement>('.js-repo-form')!
  const repoOwner = form.querySelector('.js-repository-owner-choice:checked')
  const repoName = form.querySelector('.js-repo-name')
  const repoUrl = form.querySelector('.js-repo-url')

  let valid = repoUrl ? !repoUrl.classList.contains('is-autocheck-errored') : true

  // If the repo already exists (e.g. importing into an existing empty repo) then there's no owner on the form to check
  const ownerValid = !!document.querySelector('.js-page-repo-persisted') || !!repoOwner

  valid = valid && ownerValid

  if (valid && repoName) {
    valid = repoName.classList.contains('is-autocheck-successful')
  }

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const submit = form.querySelector<HTMLButtonElement>('button[type=submit]')!
  submit.disabled = !valid
}

on('focusout', '#repository-owner', function () {
  checkOwnerSelected()
})

on('click', '.js-use-pages-url', function (e) {
  const checkbox = e.currentTarget as HTMLInputElement
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const input = document.querySelector<HTMLInputElement>('.js-pages-url-input')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const pageUrl = document.getElementById('page-url')!

  input.readOnly = checkbox.checked
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  input.value = pageUrl.textContent!
  if (checkbox.checked) {
    input.classList.add('color-fg-subtle')
    input.classList.add('color-bg-subtle')
    input.classList.remove('color-bg-default')
    input.classList.remove('color-fg-default')
  } else {
    input.classList.remove('color-fg-subtle')
    input.classList.remove('color-bg-subtle')
    input.classList.add('color-fg-default')
    input.classList.add('color-bg-default')
  }
})

on('click', '.js-privacy-toggle', function () {
  changedPrivacyManually = true
})

on('change', '.js-privacy-toggle', handlePrivacyChange)
on('details-menu-selected', '.js-owner-container', handleOwnerChange, {capture: true})

onInput('.js-repo-url', function (event) {
  const input = event.target
  if (!(input instanceof HTMLInputElement)) return

  const formGroup = input.closest('.form-group')
  if (!(formGroup instanceof HTMLDListElement)) return

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const insecureUrlWarning = document.querySelector<HTMLElement>('.js-insecure-url-warning')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const svnProtocolError = document.querySelector<HTMLElement>('.js-svn-url-error')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const gitProtocolError = document.querySelector<HTMLElement>('.js-git-url-error')!

  const url = input.value.toLowerCase()

  insecureUrlWarning.hidden = !url.startsWith('http://')
  svnProtocolError.hidden = !url.startsWith('svn://')
  gitProtocolError.hidden = !url.startsWith('git://')

  if (url.startsWith('svn://') || url.startsWith('git://')) {
    input.classList.add('is-autocheck-errored')
    formGroup.classList.add('errored')
  } else {
    input.classList.remove('is-autocheck-errored')
    formGroup.classList.remove('errored')
  }

  validate()
})

// Monitor when a menu option is selected for one of the repo
// initialization options like .gitignore or license.
on('change', '.js-repo-init-setting-menu-option', validate)
